/**
 * Media feature utility shells (upload list, marker viewer items, fullscreen,
 * slide chrome, gallery thumbnails, slideshow layout / navigation).
 */

import { chromeShell } from "./chromeShell";

import { typeShell } from "./typeShell";

export const mediaShell = {
    /* —— upload form —— */
    /* List grows with content; parent markerEditorShell.section scrolls. */
    uploadForm:
        "mt-0 mb-0 flex w-full min-w-0 max-w-full flex-col gap-3 pb-1",
    emptyState:
        `m-0 flex min-h-0 w-full flex-1 items-center justify-center text-center ${typeShell.hint}`,
    selectedList:
        "mb-0 flex w-full min-w-0 max-w-full flex-col gap-3",
    selectedEntry:
        "relative box-border flex min-h-[7.5rem] w-full min-w-0 max-w-full shrink-0 items-center gap-4 overflow-visible rounded-lg border border-solid border-border bg-surface p-4 max-[767px]:flex-col max-[767px]:items-stretch max-[767px]:gap-3 max-[767px]:p-3",
    selectedPreview:
        "relative box-border flex h-[7.5rem] min-h-[7.5rem] max-h-[7.5rem] w-[40%] min-w-0 max-w-[40%] shrink-0 items-center justify-center self-center overflow-hidden rounded-md border border-solid border-border bg-surface-muted p-3 max-[767px]:h-[6.25rem] max-[767px]:min-h-[6.25rem] max-[767px]:max-h-[6.25rem] max-[767px]:w-full max-[767px]:max-w-full max-[767px]:p-3 [&_img]:!m-0 [&_img]:block [&_img]:!h-full [&_img]:!max-h-full [&_img]:!w-full [&_img]:!max-w-full [&_img]:rounded [&_img]:object-contain [&_video]:!m-0 [&_video]:block [&_video]:!h-full [&_video]:!max-h-full [&_video]:!w-full [&_video]:!max-w-full [&_video]:rounded [&_video]:object-contain [&_audio]:h-auto [&_audio]:w-full [&_audio]:max-w-full",
    selectedContent:
        "box-border flex min-h-[7.5rem] min-w-0 w-0 max-w-full flex-1 flex-col justify-center gap-1.5 self-center overflow-visible py-0 max-[767px]:w-full max-[767px]:max-w-full max-[767px]:items-stretch max-[767px]:justify-start max-[767px]:gap-1.5 max-[767px]:p-0 max-[767px]:self-stretch",
    selectedFieldGroup: "flex min-w-0 flex-col gap-1",
    errorSlot: "m-0 min-h-4 leading-4",
    selectedTitleInput: "!min-h-12 !py-3.5",
    selectedDescInput: "!min-h-28 !py-3.5",
    removeButton:
        "absolute right-2 top-2 z-10 inline-flex min-h-6 min-w-8 cursor-pointer items-center justify-center rounded border-none bg-button-danger px-1.5 py-1 text-xs font-semibold leading-tight text-text-inverse no-underline transition-all duration-200 hover:bg-button-danger-hover hover:-translate-y-px",

    /* —— marker viewer list / item —— */
    viewerList: "mb-2 flex flex-col gap-3",
    viewerItem: "flex w-full flex-col items-center",
    viewerMedia: "h-auto w-full max-w-full rounded object-contain",
    viewerAudio: "w-full",
    viewerCaption:
        `mt-1.5 break-words text-center ${typeShell.captionSemibold}`,
    viewerDescSection: "mt-1.5 w-full text-left",
    viewerDescription:
        `break-words whitespace-pre-wrap rounded bg-surface-muted p-1.5 ${typeShell.captionMuted}`,

    /* —— fullscreen viewer —— */
    fullscreenRoot:
        "fixed left-0 top-0 z-[9999] flex h-screen w-screen items-center justify-center overflow-hidden bg-surface-immersive [transform:translateZ(0)] [will-change:transform]",
    fullscreenBox:
        "flex h-[85vh] w-[95vw] items-center justify-center overflow-hidden min-[431px]:h-[82vh] min-[431px]:w-[85vw] bp-lg:h-[80vh] bp-lg:w-[80vw] bp-xl:h-[78vh] bp-xl:w-[78vw] bp-2xl:h-[75vh] bp-2xl:w-[75vw]",
    fullscreenMedia: "h-full w-full object-contain",

    /* —— slide chrome (caption / fullscreen affordance) —— */
    fullscreenIcon:
        "absolute right-3 top-3 z-10 flex size-9 cursor-pointer items-center justify-center rounded-md border border-solid border-white/20 bg-black/70 p-1.5 text-text-inverse backdrop-blur-[4px] transition-all duration-200 hover:scale-110 hover:border-white/40 hover:bg-black/90 max-[767px]:right-2 max-[767px]:top-2 max-[767px]:size-8 max-[767px]:p-1 max-[440px]:right-2 max-[440px]:top-2 max-[440px]:size-8 max-[440px]:p-1 [&_svg]:size-icon-control",
    slideCaption:
        `mt-1 box-border w-full max-w-full shrink-0 rounded-md border border-solid border-border-light bg-surface-muted p-1 text-center max-[440px]:mb-1 max-[440px]:px-2 max-[440px]:py-1 max-[767px]:px-3 max-[767px]:py-1.5 bp-md:mt-2 bp-md:p-2 ${typeShell.captionSemibold}`,
    slideDescSection: "mt-2 flex w-full max-w-full shrink-0 flex-col text-left",
    slideDescription:
        `max-h-24 break-words whitespace-pre-wrap overflow-y-auto rounded-md border border-solid border-border-subtle bg-surface-muted p-2 text-left [overflow-wrap:anywhere] max-[440px]:p-1 max-[440px]:leading-snug max-[767px]:max-h-20 max-[767px]:px-3 max-[767px]:py-1.5 ${typeShell.captionMuted} ${chromeShell.scrollThin}`,

    /* —— slideshow layout / navigation —— */
    /* Fill InfoWindow height; do not force rem-based heights (clips inside .gm-style-iw). */
    slideshowWrapper:
        "relative box-border flex h-full min-h-0 w-full max-w-full items-center justify-center overflow-hidden px-8 py-0 bp-md:px-12",

    slideshow:
        "relative m-0 flex h-full min-h-0 max-h-full w-full max-w-full flex-1 flex-col items-center justify-center box-border overflow-hidden p-0 bp-lg:mx-auto bp-lg:max-w-[40rem]",

    closeButton: `absolute right-1 top-1 z-20 ${chromeShell.iconClose}`,

    navArea:
        "group absolute top-1 z-10 flex h-[calc(100%-0.5rem)] w-6 cursor-pointer items-center justify-center backdrop-blur-[4px] transition-all duration-300 hover:scale-105 bp-md:top-2 bp-md:h-[calc(100%-1rem)] bp-md:w-10",

    navLeft:
        "left-1 bg-[linear-gradient(90deg,rgba(255,255,255,0.9),rgba(255,255,255,0.7))] hover:bg-[linear-gradient(90deg,var(--accent-rgba-10),transparent)] bp-md:left-2 bp-md:bg-[linear-gradient(90deg,rgba(255,255,255,0.8),rgba(255,255,255,0.4))]",

    navRight:
        "right-1 bg-[linear-gradient(270deg,rgba(255,255,255,0.9),rgba(255,255,255,0.7))] hover:bg-[linear-gradient(270deg,var(--accent-rgba-10),transparent)] bp-md:right-2 bp-md:bg-[linear-gradient(270deg,rgba(255,255,255,0.8),rgba(255,255,255,0.4))]",

    navArrow:
        "flex size-8 items-center justify-center rounded-full bg-white/90 shadow-none transition-all duration-300 group-hover:scale-110 group-hover:bg-accent-10 bp-md:size-10 [&_.material-symbols-outlined]:text-icon-control [&_.material-symbols-outlined]:font-semibold [&_.material-symbols-outlined]:text-button-primary group-hover:[&_.material-symbols-outlined]:text-button-primary-hover bp-md:[&_.material-symbols-outlined]:text-icon-md",

    /** In-flow under the slide so it never covers caption / description */
    counter:
        "z-[5] mt-1 mb-2 flex min-h-6 min-w-8 shrink-0 animate-fade-in-up items-center justify-center self-center rounded-2xl border border-solid border-white/30 bg-[linear-gradient(135deg,rgba(107,114,128,0.9),rgba(75,85,99,0.8))] px-2.5 py-1 text-center text-2xs font-semibold tracking-[0.025rem] text-text-inverse shadow-none backdrop-blur-[8px] transition-all duration-200 bp-md:min-h-7 bp-md:min-w-10",

    slide: `box-border mb-0 flex min-h-0 w-full flex-1 flex-col gap-1 overflow-x-hidden overflow-y-auto text-center [aspect-ratio:unset] ${chromeShell.scrollThin}`,

    slideLoading: "flex h-full w-full items-center justify-center bg-surface-muted",
    loadingContainer: "flex flex-col items-center justify-center gap-4",
    loadingSpinner:
        "size-10 animate-spin rounded-full border-4 border-solid border-border-subtle border-t-button-primary",
    loadingText: `font-medium ${typeShell.hint}`,

    slideMetadata: "items-stretch justify-center",
    slideContent: "items-center justify-center",

    slideContentWrapper:
        "box-border flex h-full min-h-0 w-full flex-col items-center justify-center px-2 py-2 bp-md:px-2 bp-md:py-3",

    mediaItem:
        "flex h-full min-h-0 w-full max-w-full flex-col items-center gap-2 py-2 max-[440px]:gap-1 max-[440px]:py-1.5",

    imageContainer:
        "relative my-1 flex h-[60%] min-h-0 w-full max-w-full shrink-0 items-center justify-center overflow-hidden",
    videoContainer:
        "relative my-1 flex h-[60%] min-h-0 w-full max-w-full shrink-0 items-center justify-center overflow-hidden",
    audioContainer:
        "my-1 flex h-[60%] min-h-0 w-full max-w-full shrink-0 items-center justify-center p-4",
    fileContainer: "flex w-full items-center justify-center p-8 text-center",

    image:
        "h-full max-h-full min-h-0 w-auto max-w-full shrink rounded-lg object-contain shadow-none",
    video:
        "h-full max-h-full min-h-0 w-auto max-w-full shrink rounded-lg object-contain shadow-none",
    audio: "mt-3 w-full max-w-[26rem] shrink-0",

    fileLink:
        "inline-block rounded-lg border-2 border-solid border-border-light bg-surface-muted px-8 py-4 font-semibold text-button-primary no-underline transition-all duration-200 hover:-translate-y-0.5 hover:border-border-light hover:bg-border-subtle",

    /* —— thumbnail grid item —— */
    /* Item ≤ half scroll pane; media box leaves room for caption (+ user) */
    thumbItem:
        "mx-auto flex h-auto max-h-[50cqh] w-full max-w-[50cqh] cursor-pointer flex-col items-center text-center transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-none",
    thumbBox:
        "relative mb-1 flex aspect-square w-full min-h-0 max-h-[calc(50cqh-2.75rem)] items-center justify-center overflow-hidden rounded-lg bg-surface-muted transition-all duration-300 hover:bg-border-subtle",
    thumbImage:
        "h-full w-full object-cover transition-transform duration-300 [image-orientation:from-image] hover:scale-105",
    thumbVideo: "h-full w-full object-cover transition-transform duration-300 hover:scale-105",
    thumbLoading:
        `absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2 rounded bg-surface px-1.5 py-1 opacity-80 bp-md:px-2 ${typeShell.empty}`,
    thumbIconBox:
        "text-icon-empty text-text-muted transition-colors duration-200 hover:text-text-primary",
    thumbCaption:
        `mt-px max-w-full shrink-0 break-words px-px leading-tight transition-colors duration-200 group-hover:text-text-secondary bp-md:mt-0.5 ${typeShell.caption}`,
    thumbUser:
        `mt-px max-w-full shrink-0 break-words px-px leading-tight ${typeShell.captionMuted}`,
};